// Comprehensive loading state components for different scenarios

import React, { useState, useEffect } from 'react';

// Add CSS for smooth animations
const loadingStyles = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  .loading-fade-in {
    animation: fadeIn 0.3s ease-in-out;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

// Inject styles if not already present
if (typeof document !== 'undefined' && !document.getElementById('loading-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'loading-styles';
  styleSheet.textContent = loadingStyles;
  document.head.appendChild(styleSheet);
}

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'blue' | 'white' | 'gray';
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'blue',
  className = '',
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12',
  };

  const colorClasses = {
    blue: 'border-gold-500',
    white: 'border-navy-700',
    gray: 'border-navy-600',
  };

  return (
    <div
      className={`rounded-full border-2 border-t-transparent ${sizeClasses[size]} ${colorClasses[color]} ${className}`}
      style={{
        animation: 'spin 1s linear infinite'
      }}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
};

interface LoadingCardProps {
  title?: string;
  message?: string;
  showProgress?: boolean;
  progress?: number;
  className?: string;
}

export const LoadingCard: React.FC<LoadingCardProps> = ({
  title = 'Loading',
  message = 'Please wait...',
  showProgress = false,
  progress = 0,
  className = '',
}) => {
  return (
    <div className={`bg-navy-800 rounded-xl shadow-lg p-6 max-w-md w-full loading-fade-in ${className}`}>
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full mx-auto mb-4" style={{ animation: 'spin 1s linear infinite' }}></div>
        <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
        <p className="text-navy-200 mb-4">{message}</p>
        
        {showProgress && (
          <div className="w-full bg-navy-700 rounded-full h-2">
            <div
              className="bg-gold-500 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
            <p className="text-sm text-navy-300 mt-2">{Math.round(progress)}% complete</p>
          </div>
        )}
      </div>
    </div>
  );
};

interface FullScreenLoadingProps {
  title?: string;
  message?: string;
  showProgress?: boolean;
  progress?: number;
  showBackground?: boolean;
}

export const FullScreenLoading: React.FC<FullScreenLoadingProps> = ({
  title = 'Loading',
  message = 'Please wait while we prepare your experience...',
  showProgress = false,
  progress = 0,
  showBackground = true,
}) => {
  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 loading-fade-in">
      <LoadingCard
        title={title}
        message={message}
        showProgress={showProgress}
        progress={progress}
        className="relative z-10"
      />
    </div>
  );
};

interface AuthenticationLoadingProps {
  message?: string;
}

export const AuthenticationLoading: React.FC<AuthenticationLoadingProps> = ({
  message = 'Authenticating...',
}) => {
  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 loading-fade-in">
      <div className="bg-navy-800 rounded-xl shadow-lg p-8 max-w-md w-full text-center relative z-10">
        <div className="mb-6">
          <div className="bg-navy-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gold-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Vigikus Fatigue Check</h2>
        </div>
        
        <div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full mx-auto mb-4" style={{ animation: 'spin 1s linear infinite' }}></div>
        <p className="text-navy-200">{message}</p>
      </div>
    </div>
  );
};

interface TestLoadingProps {
  stage: 'initializing' | 'connecting' | 'preparing' | 'starting';
  userType: 'admin' | 'guest' | 'kiosk';
  progress?: number;
}

export const TestLoading: React.FC<TestLoadingProps> = ({
  stage,
  userType,
  progress = 0,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  // Prevent flickering by ensuring minimum display time
  useEffect(() => {
    setIsVisible(true);
  }, []);

  const getStageMessage = () => {
    switch (stage) {
      case 'initializing':
        return 'Preparing test...';
      case 'connecting':
        return 'Connecting...';
      case 'preparing':
        return 'Loading interface...';
      case 'starting':
        return 'Starting test...';
      default:
        return 'Loading...';
    }
  };

  const getTitle = () => {
    switch (userType) {
      case 'guest':
        return 'Fatigue Check';
      case 'admin':
        return 'Admin Test Interface';
      case 'kiosk':
        return 'Kiosk Test';
      default:
        return 'Test Loading';
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 loading-fade-in">
      <div className="bg-navy-800 rounded-xl shadow-lg p-8 max-w-md w-full text-center relative z-10">
        <div className="mb-6">
          <div className="bg-navy-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gold-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">{getTitle()}</h2>
        </div>
        
        <div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full mx-auto mb-4" style={{ animation: 'spin 1s linear infinite' }}></div>
        <p className="text-navy-200 mb-4">{getStageMessage()}</p>
        
        {progress > 0 && (
          <div className="w-full bg-navy-700 rounded-full h-2">
            <div
              className="bg-gold-500 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface DataLoadingProps {
  type: 'dashboard' | 'analytics' | 'history' | 'users';
  message?: string;
  showRetry?: boolean;
  onRetry?: () => void;
}

export const DataLoading: React.FC<DataLoadingProps> = ({
  type,
  message,
  showRetry = false,
  onRetry,
}) => {
  const getDefaultMessage = () => {
    switch (type) {
      case 'dashboard':
        return 'Loading dashboard data...';
      case 'analytics':
        return 'Analyzing performance data...';
      case 'history':
        return 'Loading test history...';
      case 'users':
        return 'Loading user information...';
      default:
        return 'Loading data...';
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <LoadingSpinner size="lg" className="mb-4" />
      <p className="text-navy-200 mb-4">{message || getDefaultMessage()}</p>
      
      {showRetry && onRetry && (
        <button
          onClick={onRetry}
          className="text-gold-400 hover:text-gold-300 text-sm font-medium"
        >
          Retry loading
        </button>
      )}
    </div>
  );
};

interface InlineLoadingProps {
  message?: string;
  size?: 'sm' | 'md';
  className?: string;
}

export const InlineLoading: React.FC<InlineLoadingProps> = ({
  message = 'Loading...',
  size = 'sm',
  className = '',
}) => {
  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <LoadingSpinner size={size} />
      <span className="text-navy-200 text-sm">{message}</span>
    </div>
  );
};

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'text',
  width,
  height,
}) => {
  const baseClasses = 'animate-pulse bg-navy-700';
  
  const variantClasses = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-md',
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={style}
    />
  );
};

interface SkeletonCardProps {
  showAvatar?: boolean;
  lines?: number;
  className?: string;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  showAvatar = false,
  lines = 3,
  className = '',
}) => {
  return (
    <div className={`bg-navy-800 rounded-lg shadow p-6 ${className}`}>
      <div className="flex items-start space-x-4">
        {showAvatar && (
          <Skeleton variant="circular" width={40} height={40} />
        )}
        <div className="flex-1 space-y-2">
          <Skeleton height={20} width="75%" />
          {Array.from({ length: lines }).map((_, index) => (
            <Skeleton
              key={index}
              height={16}
              width={index === lines - 1 ? '50%' : '100%'}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

interface LoadingOverlayProps {
  isVisible: boolean;
  message?: string;
  progress?: number;
  showProgress?: boolean;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isVisible,
  message = 'Processing...',
  progress = 0,
  showProgress = false,
}) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-navy-800 rounded-xl shadow-lg p-6 max-w-sm w-full mx-4">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p className="text-navy-100 mb-4">{message}</p>
          
          {showProgress && (
            <div className="w-full bg-navy-700 rounded-full h-2">
              <div
                className="bg-gold-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              />
              <p className="text-sm text-navy-300 mt-2">{Math.round(progress)}% complete</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
